import ExcelJS from "exceljs"; // for ExcelJS
import FileSaver from "file-saver";
import { getStudentEmergencyRelations, getStudentRelations } from "../../../../modules/common/shared/Util";
import { fetchAcademicYears } from "../../../academicOperations/services/academicYearsService";
import { fetchGrades } from "../../../academicOperations/services/gradesService";
import { AcademicYearStatus, Grade } from "../../../academicOperations/types";
import { fetchFeeStructures } from "../../../fees/services/feeStructureService";

const TERMS_PER_YEAR = 4;
const GRADUATE = "Graduate";
const DUES_PREFIX = "Dues ";
/** Template sheet names, shared with the upload checks. */
export const SHEETS = {
  instructions: "Instructions",
  gradeSection: "Grade & Section", // B3 = Grade, B4 = Section for every student in the file
  students: "Students",
};

const startYear = (academicYearName: string) => parseInt(academicYearName.substring(0, 4), 10);

/** Dues column header for an academic year, e.g. "Dues 2023-24". */
export const duesHeader = (academicYearName: string) => `${DUES_PREFIX}${academicYearName}`;

/** Dues limit for one year, for a student by current grade. */
export interface DuesLimit {
  gradeThen: string | null; // grade the student was in that year; null when it would be below the lowest grade
  max: number | null; // term fee × 4 of gradeThen; null when no fee is set for that grade and year
}

export interface DuesRules {
  duesYears: string[]; // years with a dues column, oldest first
  skippedYears: string[]; // ACTIVE/CLOSED years with no fee structure at all - they get no dues column
  orderedGrades: Grade[]; // promotion order, lowest (PRE-KG) first, without Graduate
  limits: Record<string, Record<string, DuesLimit>>; // limits[currentGradeName][yearName]
  missingFees: Record<string, string[]>; // missingFees[yearName] = grades with no fee set that year
  currentYear: string | null; // the ACTIVE academic year; its dues are required on import
}

/**
 * Everything the dues columns need, from cached data: the years that get a column (ACTIVE and CLOSED
 * with at least one fee set; never PLANNED/INITIALIZED), the grade order, and for each current grade +
 * year the grade the student was in that year (current grade minus the years back from the latest
 * year) and its year total.
 */
export const loadDuesRules = async (): Promise<DuesRules> => {
  const [years, grades] = await Promise.all([fetchAcademicYears(), fetchGrades()]);
  const allYears = years
    .filter((y) => y.status === AcademicYearStatus.ACTIVE || y.status === AcademicYearStatus.CLOSED)
    .map((y) => y.name)
    .sort((a, b) => startYear(a) - startYear(b));
  // Copy before sorting: the grades list is shared through the cache
  const orderedGrades: Grade[] = [...grades]
    .filter((g) => g.name !== GRADUATE)
    .sort((a, b) => (a.numericValue ?? Number.MAX_SAFE_INTEGER) - (b.numericValue ?? Number.MAX_SAFE_INTEGER));

  const feesByYear = await Promise.all(allYears.map((year) => fetchFeeStructures(year)));
  const feeOf = (yearIndex: number, grade: Grade) => feesByYear[yearIndex].find((f) => f.gradeId === grade.id)?.fee;

  // Years back are counted over ALL years, so a skipped year in between doesn't shift the grades
  const duesYears: string[] = [];
  const skippedYears: string[] = [];
  const limits: DuesRules["limits"] = {};
  const missingFees: DuesRules["missingFees"] = {};
  orderedGrades.forEach((grade) => (limits[grade.name] = {}));

  allYears.forEach((year, yearIndex) => {
    if (!orderedGrades.some((g) => feeOf(yearIndex, g))) {
      skippedYears.push(year);
      return;
    }
    duesYears.push(year);
    missingFees[year] = orderedGrades.filter((g) => !feeOf(yearIndex, g)).map((g) => g.name);
    const yearsBack = allYears.length - 1 - yearIndex;
    orderedGrades.forEach((grade, gradeIndex) => {
      const gradeThen = orderedGrades[gradeIndex - yearsBack];
      const fee = gradeThen ? feeOf(yearIndex, gradeThen) : undefined;
      limits[grade.name][year] = { gradeThen: gradeThen?.name ?? null, max: fee ? fee * TERMS_PER_YEAR : null };
    });
  });

  const currentYear = years.find((y) => y.status === AcademicYearStatus.ACTIVE)?.name ?? null;
  return { duesYears, skippedYears, orderedGrades, limits, missingFees, currentYear };
};

const formatAmount = (amount: number) => amount.toLocaleString("en-IN");

/** Dues columns of a template made for one grade, and the years left out with the reason. */
export interface GradeDuesPlan {
  columns: { year: string; gradeThen: string; max: number; required: boolean }[]; // oldest first; required = current year
  excluded: { year: string; reason: string; fixable: boolean }[]; // fixable: setting a fee would add the column
  // Why this grade can't be imported at all (no ACTIVE year, or no current-year fee); null when it can
  blocker: string | null;
}

/**
 * Which dues columns a template for `gradeName` gets: one per year where a student of that grade can
 * owe something - the grade that year exists and has a fee set. Every other year is left out.
 */
export const duesPlanForGrade = (rules: DuesRules, gradeName: string): GradeDuesPlan => {
  const lowest = rules.orderedGrades[0]?.name ?? "the lowest grade";
  const columns: GradeDuesPlan["columns"] = [];
  const excluded: GradeDuesPlan["excluded"] = rules.skippedYears.map((year) => ({
    year,
    reason: `no fee structure is set for ${year}`,
    fixable: true,
  }));

  rules.duesYears.forEach((year) => {
    const limit = rules.limits[gradeName]?.[year];
    if (!limit) return;
    if (limit.gradeThen === null) excluded.push({ year, reason: `a ${gradeName} student would have been below ${lowest}`, fixable: false });
    else if (limit.max === null) excluded.push({ year, reason: `no fee is set for ${limit.gradeThen} in ${year}`, fixable: true });
    else columns.push({ year, gradeThen: limit.gradeThen, max: limit.max, required: year === rules.currentYear });
  });

  excluded.sort((a, b) => startYear(a.year) - startYear(b.year));

  // The current-year dues are required, so the template is useless without that column
  let blocker: string | null = null;
  if (!rules.currentYear) blocker = "There is no ACTIVE academic year, so students can't be imported.";
  else if (!columns.some((c) => c.required))
    blocker = `No fee is set for Grade ${gradeName} in ${rules.currentYear} (the current year), which every imported student needs. Set it in Finance → Fee Structures first.`;

  return { columns, excluded, blocker };
};

/** Instructions sheet: every rule the upload checks, grouped by topic. */
const writeInstructions = (workbook: ExcelJS.Workbook, rules: DuesRules, grade: string, sectionName: string, plan: GradeDuesPlan) => {
  const sheet = workbook.addWorksheet(SHEETS.instructions);
  sheet.getColumn(1).width = 150;
  sheet.getColumn(1).alignment = { wrapText: true, vertical: "top" };

  const title = (text: string) => {
    sheet.addRow([text]);
    sheet.getRow(sheet.rowCount).font = { bold: true, size: 14 };
  };
  const section = (text: string) => {
    sheet.addRow([]);
    sheet.addRow([text]);
    const row = sheet.getRow(sheet.rowCount);
    row.font = { bold: true, color: { argb: "FFFFFFFF" } };
    row.getCell(1).fill = { type: "pattern", pattern: "solid", fgColor: { argb: "FF1F4E78" } };
  };
  const line = (text: string) => sheet.addRow([text]);

  const lowest = rules.orderedGrades[0]?.name ?? "the lowest grade";

  title(`STUDENT BULK IMPORT – GRADE ${grade}, SECTION ${sectionName}`);
  line("Read all sections before filling the file. Every rule below is checked again when the file is uploaded; rows with problems are listed so you can fix them.");

  section("1. STEPS");
  line(`Step 1: This template is for Grade ${grade}, Section ${sectionName} (shown on the '${SHEETS.gradeSection}' sheet). Every student in this file is added there. For another grade or section, download another template.`);
  line(`Step 2: On the '${SHEETS.students}' sheet, enter one row per student, starting from row 2.`);
  line("Step 3: Save the file (.xlsx) and upload it on Students → Import.");
  line("Download a fresh template whenever academic years or fee structures change.");

  section("2. GENERAL");
  line("Columns marked with * are required.");
  line("Do not rename, add, move or remove columns, and do not rename the sheets.");
  line("Completely empty rows are ignored. A row with any value in it is treated as a student.");
  line("Admission No must be unique in the file. A student whose Admission No already exists in the system is not imported.");

  section("3. STUDENT");
  line("Gender: select Male, Female or Other.");
  line("DOB(DD-MMM-YYYY): a real date in DD-MMM-YYYY format, e.g. 12-May-2010. It cannot be a future date. The column is formatted as text so Excel keeps this format.");
  line("PlaceOfBirth is required. Religion, Nationality, MotherTongue and BloodGroup are optional.");

  section("4. ADDRESS");
  line("Present address: House No, Street Road, City, State and Pincode are required. Area Locality and LandMark are optional.");
  line("Present - Same As Permanent: select 'Yes' to copy the present address into the permanent address columns automatically, or 'No' to fill the permanent address yourself.");
  line("Pincode must be 6 digits.");

  section("5. GUARDIAN AND PARENTS");
  line("Guardian Type: select Father, Mother or Other.");
  line("Father / Mother: the guardian's details are copied into the Father or Mother columns, and Guardian - Relation is filled for you.");
  line("Other: the guardian is neither parent. Select Guardian - Relation yourself (e.g. Grandfather, Uncle) and fill in the Father and Mother columns.");
  line("Father - Name and Mother - Name are required, except for the parent who is the guardian (copied automatically).");
  line("Guardian - Name and Guardian - Mobile are required.");

  section("6. EMERGENCY CONTACT");
  line("Emergency - Relation is required.");
  line("Father, Mother or Guardian: that person's details are copied into the Emergency columns automatically, so their name and mobile must be filled in.");
  line("Any other relation (Aunt, Brother, ...): fill in Emergency - Name and Emergency - Mobile yourself.");

  section("7. CONTACT DETAILS");
  line("Mobile numbers (Guardian, Father, Mother, Emergency): exactly 10 digits, starting with 7, 8 or 9. Mobile columns are formatted as text - keep them that way.");
  line("Email (optional): a valid email address, e.g. name@example.com.");
  line("Monthly Income (optional): a whole number, 0 or more (no decimals).");

  section("8. DUES");
  line("Each dues cell is the amount the student still owes for that academic year. Enter 0 if that year is fully paid.");
  if (rules.currentYear) {
    line(`Dues ${rules.currentYear} (the current year) is REQUIRED for every student - every imported student is enrolled in ${rules.currentYear}.`);
  }
  line("Past years: leave the cell blank if the student was not in the school that year.");
  line("On import, the student is created as ACTIVE with an enrollment for every year, and the dues for each year are split into 4 terms: the paid amount clears Term 1 first, then Term 2, and so on.");
  line("Whole numbers only, up to the maximum below: that year's total fee (term fee x 4) for the grade the student was in that year.");
  line(`The grade for a past year is the current grade minus the years back (e.g. a Grade One student was in UKG last year). Years before ${lowest} cannot have dues.`);
  if (plan.columns.length === 0) {
    line("This template has no dues columns.");
  } else {
    line(`Dues columns in this template (Grade ${grade}):`);
    plan.columns.forEach((c) =>
      line(`   ${duesHeader(c.year)}: Grade ${c.gradeThen} that year, maximum ${formatAmount(c.max)}${c.required ? " - REQUIRED" : ""}`)
    );
  }
  if (plan.excluded.length > 0) {
    line("Years with no dues column:");
    plan.excluded.forEach((e) => line(`   ${e.year}: ${e.reason}`));
    line("To add a missing year, set its fee in Finance → Fee Structures and download the template again.");
  }

  section("9. AUTO-FILLED COLUMNS");
  line("Permanent address, Father / Mother (from Guardian), Guardian - Relation and the Emergency columns are filled by formulas.");
  line("Opening the file in Excel recalculates them. You can type over an auto-filled cell if needed.");
  line("Values pasted into cells skip Excel's own checks (lists, limits). They are still checked on upload.");

  section("10. AFTER UPLOAD");
  line("Every row is checked. Problems are listed with the Excel row number and can be downloaded as a sheet.");
  line("Nothing is imported until all problems are fixed. Students that fail on the server can be downloaded with the reason, fixed and imported again.");
};

/**
 * Template for one grade + section, chosen on the import page. Making it per grade lets every dues
 * column carry a fixed maximum (shown in the cell prompt), instead of a lookup on a grade picked later.
 */
export const downloadStudentTemplate = async (grade: string, sectionName: string) => {
  const rules = await loadDuesRules();
  const plan = duesPlanForGrade(rules, grade);

  const workbook = new ExcelJS.Workbook();

  // ===============================
  // INSTRUCTION SHEET
  // ===============================
  writeInstructions(workbook, rules, grade, sectionName, plan);

  // ===============================
  // GRADE & SECTION SHEET - filled in and protected; read on upload
  // ===============================
  const infoSheet = workbook.addWorksheet(SHEETS.gradeSection);

  infoSheet.getCell("A1").value = "Grade and Section for every student in this file";
  infoSheet.getCell("A1").font = { bold: true, size: 14 };

  infoSheet.getCell("A3").value = "Grade";
  infoSheet.getCell("B3").value = grade;
  infoSheet.getCell("A4").value = "Section";
  infoSheet.getCell("B4").value = sectionName;
  ["B3", "B4"].forEach((address) => (infoSheet.getCell(address).font = { bold: true }));
  infoSheet.getCell("A6").value = "To import into another grade or section, download a new template from Students → Import.";
  infoSheet.getCell("A6").font = { italic: true, color: { argb: "FF666666" } };

  infoSheet.getColumn("A").width = 25;
  infoSheet.getColumn("B").width = 30;
  await infoSheet.protect("", { selectLockedCells: true, selectUnlockedCells: true });

  // ===============================
  // STUDENT SHEET
  // ===============================
  const studentsSheet = workbook.addWorksheet(SHEETS.students, {
  });

  // Headers
  const headers = [
    "Name *",
    "AdmissionNo *",
    "Gender *",
    "DOB(DD-MMM-YYYY) *",
    "PlaceOfBirth *",
    "Religion",
    "Nationality",
    "MotherTongue",
    "BloodGroup",
    "Present - House No *",
    "Present - Street Road *",
    "Present - Area Locality",
    "Present - LandMark",
    "Present - City *",
    "Present - State *",
    "Present - Pincode *",
    "Present - Same As Permanent *",
    "Permanent - House No *",
    "Permanent - Street Road *",
    "Permanent - Area Locality",
    "Permanent - LandMark",
    "Permanent - City *",
    "Permanent - State *",
    "Permanent - Pincode *",
    "Guardian Type *",
    "Guardian - Name *",
    "Guardian - DOB",
    "Guardian - Qualification",
    "Guardian - Occupation",
    "Guardian - Monthly Income",
    "Guardian - Mobile *",
    "Guardian - Email",
    "Guardian - Relation *",
    "Father - Name *",
    "Father - DOB",
    "Father - Qualification",
    "Father - Occupation",
    "Father - Monthly Income",
    "Father - Mobile",
    "Father - Email",
    "Mother - Name *",
    "Mother - DOB",
    "Mother - Qualification",
    "Mother - Occupation",
    "Mother - Monthly Income",
    "Mother - Mobile",
    "Mother - Email",
    "Emergency - Relation *",
    "Emergency - Name *",
    "Emergency - DOB",
    "Emergency - Qualification",
    "Emergency - Occupation",
    "Emergency - Monthly Income",
    "Emergency - Mobile *",
    "Emergency - Email",
    ...plan.columns.map((c) => duesHeader(c.year)),
  ];

  studentsSheet.addRow(headers);

  // Header styling
  studentsSheet.getRow(1).eachCell((cell) => {
    cell.font = { bold: true, color: { argb: "FFFFFFFF" } };
    cell.fill = {
      type: "pattern",
      pattern: "solid",
      fgColor: { argb: "1F4E78" },
    };
    cell.alignment = { vertical: "middle", horizontal: "center" };
    cell.border = {
      top: { style: "thin" },
      left: { style: "thin" },
      bottom: { style: "thin" },
      right: { style: "thin" },
    };
  });

  // Column widths
  headers.forEach((_, index) => {
    studentsSheet.getColumn(index + 1).width = 25;
  });

  // ===============================
  // COLUMN LOOKUP HELPER
  // (resolves a column letter from its header text instead of a hardcoded index/letter,
  // so inserting/removing/reordering headers above never requires manually recalculating
  // every downstream column reference)
  // ===============================
  const colLetter = (header: string) => {
    const index = headers.indexOf(header);
    if (index === -1) throw new Error(`Unknown header: ${header}`);
    return studentsSheet.getColumn(index + 1).letter;
  };

  // ===============================
  // TEXT FORMAT FOR DOB & MOBILE
  // ===============================
  const dobColumnLetter = colLetter("DOB(DD-MMM-YYYY) *");
  const mobileHeaders = ["Guardian - Mobile *", "Father - Mobile", "Mother - Mobile", "Emergency - Mobile *"];
  [dobColumnLetter, ...mobileHeaders.map(colLetter)].forEach((letter) => {
    studentsSheet.getColumn(letter).numFmt = "@";
  });

  // ===============================
  // DOB FORMAT VALIDATION (DD-MMM-YYYY)
  // ===============================
  // Registered as a single explicit range (not a per-cell loop) - exceljs's per-cell auto-merge
  // sorts cell addresses lexicographically, not numerically, and produces corrupted/overlapping
  // sqref ranges once row numbers reach double digits when the same validation is reused across
  // cells. A single range with a relative formula avoids that entirely; Excel adjusts the D2
  // reference per row on its own.
  // exceljs's type defs don't expose `worksheet.dataValidations` even though it exists at runtime
  (studentsSheet as any).dataValidations.add(`${dobColumnLetter}2:${dobColumnLetter}1000`, {
    type: "custom",
    allowBlank: true,
    formulae: [
      `=AND(LEN(${dobColumnLetter}2)=11,
            MID(${dobColumnLetter}2,3,1)="-",
            MID(${dobColumnLetter}2,7,1)="-",
            ISNUMBER(VALUE(LEFT(${dobColumnLetter}2,2))),
            ISNUMBER(VALUE(RIGHT(${dobColumnLetter}2,4))),
            IFERROR(ISNUMBER(DATEVALUE(${dobColumnLetter}2)),FALSE))`,
    ],
    showErrorMessage: true,
    errorTitle: "Invalid Date of Birth",
    error: "DOB must be in DD-MMM-YYYY format (e.g. 12-May-2010)",
  });

  mobileHeaders.forEach((header) => {
    const columnLetter = colLetter(header);

    // Apply validation from row 2 to row 1000 (adjust as needed)
    for (let row = 2; row <= 1000; row++) {
      const cell = studentsSheet.getCell(`${columnLetter}${row}`);

      cell.dataValidation = {
        type: "custom",
        allowBlank: true,
        formulae: [
          `=AND(LEN(${columnLetter}${row})=10,
              ISNUMBER(${columnLetter}${row}*1),
              LEFT(${columnLetter}${row},1)>="7",
              LEFT(${columnLetter}${row},1)<="9")`,
        ],
        showErrorMessage: true,
        errorTitle: "Invalid Mobile Number",
        error: "Mobile must be exactly 10 digits and start with 7, 8, or 9",
      };
    }
  });

  // ===============================
  // DUES PER ACADEMIC YEAR (whole number, 0 to that year's total fee)
  // The template is for one grade, so each column has a fixed maximum: term fee × 4 of the grade the
  // student was in that year. Pasted values skip Excel validation, so the same check runs again on
  // upload and in the backend.
  // ===============================
  plan.columns.forEach(({ year, gradeThen, max, required }) => {
    const duesLetter = colLetter(duesHeader(year));
    (studentsSheet as any).dataValidations.add(`${duesLetter}2:${duesLetter}1000`, {
      type: "whole",
      operator: "between",
      // Current year is required; Excel only enforces this when a cell is edited, the upload checks every row
      allowBlank: !required,
      formulae: [0, max],
      showInputMessage: true,
      promptTitle: `${duesHeader(year)} (max ${formatAmount(max)})`,
      // Plain sentences: line breaks in a prompt can show as spaces in Excel
      prompt: required
        ? `Required. Amount still owed for ${year}, the current year (Grade ${gradeThen}). Enter 0 if fully paid. Maximum: ${formatAmount(max)}.`
        : `Amount still owed for ${year} (Grade ${gradeThen} that year). Leave blank if not in school that year. Enter 0 if fully paid. Maximum: ${formatAmount(max)}.`,
      showErrorMessage: true,
      errorTitle: `${duesHeader(year)}: invalid amount`,
      error: required
        ? `Required: enter a whole number from 0 to ${formatAmount(max)} (the ${year} total fee for Grade ${gradeThen}).`
        : `Enter a whole number from 0 to ${formatAmount(max)} (the ${year} total fee for Grade ${gradeThen}), or leave it blank.`,
    });
  });

  // ===============================
  // PRESENT ADDRESS SAME AS PERMANENT
  // Yes/No dropdown (default "No"); selecting "Yes" copies each Present Address
  // field into its Permanent Address counterpart via a live formula. Selecting "No"
  // (or leaving it) clears the formula's result to "" so Permanent fields stay
  // independently editable.
  // ===============================
  const sameAsPermanentLetter = colLetter("Present - Same As Permanent *");

  (studentsSheet as any).dataValidations.add(`${sameAsPermanentLetter}2:${sameAsPermanentLetter}1000`, {
    type: "list",
    allowBlank: false,
    formulae: ['"Yes,No"'],
    showErrorMessage: true,
    errorTitle: "Invalid selection",
    error: "Please select Yes or No",
  });

  const presentToPermanent: [string, string][] = [
    ["Present - House No *", "Permanent - House No *"],
    ["Present - Street Road *", "Permanent - Street Road *"],
    ["Present - Area Locality", "Permanent - Area Locality"],
    ["Present - LandMark", "Permanent - LandMark"],
    ["Present - City *", "Permanent - City *"],
    ["Present - State *", "Permanent - State *"],
    ["Present - Pincode *", "Permanent - Pincode *"],
  ];
  const permanentFormulaColumns = presentToPermanent.map(
    ([presentHeader, permanentHeader]) => [colLetter(presentHeader), colLetter(permanentHeader)] as [string, string]
  );

  for (let row = 2; row <= 1000; row++) {
    // Left blank on purpose - only rows the admin actually fills in should show a value;
    // an untouched row shouldn't display "No". The formula below treats blank the same as
    // "No" (falls through to ""), and the required-field check forces an explicit Yes/No
    // once the admin starts filling that row.
    permanentFormulaColumns.forEach(([presentLetter, permanentLetter]) => {
      studentsSheet.getCell(`${permanentLetter}${row}`).value = {
        // A bare cell reference to a blank Present cell would otherwise show 0 (default
        // Excel behaviour), so blank is checked explicitly and mapped to "".
        formula: `IF(AND($${sameAsPermanentLetter}${row}="Yes",${presentLetter}${row}<>""),${presentLetter}${row},"")`,
        result: "",
      };
    });
  }

  // ===============================
  // DROPDOWN VALIDATION
  // ===============================
  // Registered as single explicit ranges (see the DOB validation comment above for why a
  // per-cell loop with identical formulae per cell is unsafe with exceljs's auto-merge).
  const genderColumnLetter = colLetter("Gender *");
  const guardianRelationLetter = colLetter("Guardian - Relation *");
  const emergencyRelationLetter = colLetter("Emergency - Relation *");
  const relations = getStudentRelations().join(",");
  const emergencyRelations = getStudentEmergencyRelations().join(",");

  (studentsSheet as any).dataValidations.add(`${genderColumnLetter}2:${genderColumnLetter}1000`, {
    type: "list",
    allowBlank: false,
    formulae: ['"Male,Female,Other"'],
  });

  (studentsSheet as any).dataValidations.add(`${guardianRelationLetter}2:${guardianRelationLetter}1000`, {
    type: "list",
    allowBlank: false,
    formulae: [`"${relations}"`],
  });

  (studentsSheet as any).dataValidations.add(`${emergencyRelationLetter}2:${emergencyRelationLetter}1000`, {
    type: "list",
    allowBlank: false,
    formulae: [`"${emergencyRelations}"`],
  });

  // ===============================
  // GUARDIAN TYPE AUTO-COPY (Father / Mother)
  // Guardian Type dropdown (Father/Mother/Other). Selecting Father copies each Guardian
  // field into its Father counterpart via a live formula; selecting Mother does the same
  // for Mother. Selecting Other leaves both independently editable.
  //
  // It also drives Guardian - Relation *: Father/Mother auto-fills "Father"/"Mother" there
  // (matching the selection), Other leaves it blank for manual entry. Note: exceljs/Excel
  // cannot make a cell conditionally read-only based on another cell's value without VBA
  // macros (which this template intentionally avoids - see prior DOB date-picker decision),
  // so this is an auto-fill, not an enforced lock; typing over it is still possible the same
  // way it is for every other auto-copied field in this sheet.
  // ===============================
  const guardianTypeLetter = colLetter("Guardian Type *");

  (studentsSheet as any).dataValidations.add(`${guardianTypeLetter}2:${guardianTypeLetter}1000`, {
    type: "list",
    allowBlank: false,
    formulae: ['"Father,Mother,Other"'],
    showErrorMessage: true,
    errorTitle: "Invalid selection",
    error: "Please select Father, Mother or Other",
  });

  const guardianSubFields: [string, string, string][] = [
    ["Guardian - Name *", "Father - Name *", "Mother - Name *"],
    ["Guardian - DOB", "Father - DOB", "Mother - DOB"],
    ["Guardian - Qualification", "Father - Qualification", "Mother - Qualification"],
    ["Guardian - Occupation", "Father - Occupation", "Mother - Occupation"],
    ["Guardian - Monthly Income", "Father - Monthly Income", "Mother - Monthly Income"],
    ["Guardian - Mobile *", "Father - Mobile", "Mother - Mobile"],
    ["Guardian - Email", "Father - Email", "Mother - Email"],
  ];
  const guardianCopyColumns = guardianSubFields.map(
    ([guardianHeader, fatherHeader, motherHeader]) =>
      [colLetter(guardianHeader), colLetter(fatherHeader), colLetter(motherHeader)] as [string, string, string]
  );

  // ===============================
  // EMERGENCY RELATION AUTO-COPY (Father / Mother / Guardian)
  // Selecting Father/Mother/Guardian as the Emergency Relation copies that person's
  // fields into the Emergency fields via a live formula. Any other relation (Aunt,
  // Brother, etc.) leaves the Emergency fields independently editable.
  // ===============================
  const emergencySubFields: [string, string, string, string][] = [
    ["Emergency - Name *", "Father - Name *", "Mother - Name *", "Guardian - Name *"],
    ["Emergency - DOB", "Father - DOB", "Mother - DOB", "Guardian - DOB"],
    ["Emergency - Qualification", "Father - Qualification", "Mother - Qualification", "Guardian - Qualification"],
    ["Emergency - Occupation", "Father - Occupation", "Mother - Occupation", "Guardian - Occupation"],
    ["Emergency - Monthly Income", "Father - Monthly Income", "Mother - Monthly Income", "Guardian - Monthly Income"],
    ["Emergency - Mobile *", "Father - Mobile", "Mother - Mobile", "Guardian - Mobile *"],
    ["Emergency - Email", "Father - Email", "Mother - Email", "Guardian - Email"],
  ];
  const emergencyCopyColumns = emergencySubFields.map(
    ([emergencyHeader, fatherHeader, motherHeader, guardianHeader]) =>
      [colLetter(emergencyHeader), colLetter(fatherHeader), colLetter(motherHeader), colLetter(guardianHeader)] as [
        string,
        string,
        string,
        string
      ]
  );

  for (let row = 2; row <= 1000; row++) {
    studentsSheet.getCell(`${guardianRelationLetter}${row}`).value = {
      formula: `IF($${guardianTypeLetter}${row}="Father","Father",IF($${guardianTypeLetter}${row}="Mother","Mother",""))`,
      result: "",
    };

    guardianCopyColumns.forEach(([guardianLetter, fatherLetter, motherLetter]) => {
      studentsSheet.getCell(`${fatherLetter}${row}`).value = {
        formula: `IF(AND($${guardianTypeLetter}${row}="Father",${guardianLetter}${row}<>""),${guardianLetter}${row},"")`,
        result: "",
      };
      studentsSheet.getCell(`${motherLetter}${row}`).value = {
        formula: `IF(AND($${guardianTypeLetter}${row}="Mother",${guardianLetter}${row}<>""),${guardianLetter}${row},"")`,
        result: "",
      };
    });

    emergencyCopyColumns.forEach(([emergencyLetter, fatherLetter, motherLetter, guardianLetter]) => {
      studentsSheet.getCell(`${emergencyLetter}${row}`).value = {
        formula:
          `IF(AND($${emergencyRelationLetter}${row}="Father",${fatherLetter}${row}<>""),${fatherLetter}${row},` +
          `IF(AND($${emergencyRelationLetter}${row}="Mother",${motherLetter}${row}<>""),${motherLetter}${row},` +
          `IF(AND($${emergencyRelationLetter}${row}="Guardian",${guardianLetter}${row}<>""),${guardianLetter}${row},"")))`,
        result: "",
      };
    });
  }

  // ===============================
  // EXPORT FILE
  // ===============================
  const buffer = await workbook.xlsx.writeBuffer();

  const blob = new Blob([buffer], {
    type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  });

  FileSaver.saveAs(blob, `Student_Import_${grade}_${sectionName}.xlsx`.replace(/[^\w.-]+/g, "_"));
};
